<script lang="ts">
  import { autofocus } from '#app/lib/components/actions/autofocus'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { normalizeError } from '#app/lib/error'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { onChange } from '#app/lib/svelte/svelte'
  import { loadInternalLink } from '#app/lib/utils'
  import type { KeyUpEvent } from '#app/types/declarations/svelte'
  import Spinner from '#components/spinner.svelte'
  import { createEditionFromWork, validateEditionPossibility, getAddWithoutIsbnPath } from '#entities/components/lib/edition_creation_helpers'
  import { showEntity } from '#entities/entities'
  import type { SerializedEntity } from '#entities/lib/entities'
  import type { RelativeUrl } from '#server/types/common'
  import { i18n, I18n } from '#user/lib/i18n'

  const {
    work,
    editions,
  }: {
    work: SerializedEntity
    editions: SerializedEntity[]
  } = $props()

  let userInput = $state('')
  let flash: FlashState | undefined = $state()
  let showForm: boolean | undefined = $state()
  let creatingFromIsbn = $state(false)

  async function onIsbnButtonClick () {
    creatingFromIsbn = true
    flash = undefined
    try {
      validateEditionPossibility(userInput, editions)
      const newEdition = await createEditionFromWork({
        workEntity: work,
        userInput,
      })
      await showEntity(newEdition.uri)
    } catch (err) {
      flash = normalizeError(err)
    } finally {
      creatingFromIsbn = false
    }
  }

  function onInputKeyup (e: KeyUpEvent) {
    const key = getActionKey(e)
    if (key === 'esc') showForm = false
  }

  let addWithoutIsbnPath: RelativeUrl | undefined = $state()

  $effect(() => {
    onChange(work, () => {
      getAddWithoutIsbnPath(work)
        .then(res => addWithoutIsbnPath = res)
        .catch(err => flash = err)
    })
  })
</script>

<div class="wrapper">
  <div class="edition-creation">
    {#if showForm}
      <div class="from-isbn">
        <label for="isbn-group">
          {i18n('Add an edition by its ISBN')}
        </label>
        <form class="isbn-group">
          <input
            type="text"
            name="isbn"
            class="has-alertbox"
            bind:value={userInput}
            placeholder="ex: 2070368228"
            onkeyup={onInputKeyup}
            aria-label="isbn"
            use:autofocus
          />
          <button
            class="isbn-button tiny-button grey"
            disabled={creatingFromIsbn}
            onclick={onIsbnButtonClick}
          >
            {#if creatingFromIsbn}
              <Spinner />
            {:else}
              {@html icon('plus')}
            {/if}
            {i18n('add')}
          </button>
        </form>
      </div>
      {#if addWithoutIsbnPath}
        <a
          href={addWithoutIsbnPath}
          class="without-isbn tiny-button grey"
          onclick={loadInternalLink}
        >
          {@html icon('plus')}
          {I18n('add an edition without an ISBN')}
        </a>
      {:else}
        <Spinner />
      {/if}
    {:else}
      <button
        class="tiny-button show-form"
        onclick={() => showForm = true}
      >
        {@html icon('plus')}
        {I18n('add a missing edition')}
      </button>
    {/if}
  </div>
  <Flash bind:state={flash} />
</div>

<style lang="scss">
  @import "general/scss/utils";
  .wrapper{
    padding: 1em 0.5em 0.5em;
    align-self: stretch;
  }
  .edition-creation{
    @include display-flex(column, stretch, center);
    @include radius;
    max-inline-size: 20em;
    margin: 0 auto;
  }
  .isbn-group{
    @include display-flex(row);
    input{
      @include radius-right(0);
    }
    .tiny-button{
      @include radius-left(0);
    }
    margin-block-end: 0.5em;
  }
  .isbn-button{
    white-space: nowrap;
  }
  .without-isbn{
    padding: 0.5em;
    line-height: 1rem;
    text-align: center;
  }
  .show-form{
    max-inline-size: 20em;
  }
</style>
